import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import api, { formatApiError, resolveImage } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Edit, Trash2, X, Loader2 } from "lucide-react";

function ArticleForm({ initial, cats, onCancel, onSaved }) {
  const { t } = useTranslation();
  const [f, setF] = useState({ title_zh: "", title_en: "", body_zh: "", body_en: "", cover_image: "", category: cats[0]?.key || "love", active: true, ...(initial || {}) });
  const [saving, setSaving] = useState(false);
  const inputCls = "w-full px-4 py-2.5 rounded-md border border-[#e5ebeb] focus:border-[#00868e] outline-none bg-white";
  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (initial?.id) await api.patch(`/admin/articles/${initial.id}`, f);
      else await api.post("/admin/articles", f);
      toast.success(t("common.saved"));
      onSaved();
    } catch (err) { toast.error(formatApiError(err)); } finally { setSaving(false); }
  };
  return (
    <form onSubmit={submit} className="rounded-lg border border-[#e5ebeb] bg-white p-6 space-y-3" data-testid="article-form">
      <div className="flex justify-between"><h2 className="font-serif-cn text-xl text-[#003645] font-semibold">{initial?.id ? t("adminP3.editArticle") : t("adminP3.newArticle")}</h2><button type="button" onClick={onCancel}><X size={16} /></button></div>
      <div className="grid sm:grid-cols-2 gap-3">
        <input required placeholder="Title ZH" value={f.title_zh} onChange={(e) => setF({ ...f, title_zh: e.target.value })} className={inputCls} data-testid="af-title-zh" />
        <input required placeholder="Title EN" value={f.title_en} onChange={(e) => setF({ ...f, title_en: e.target.value })} className={inputCls} />
        <textarea rows={6} placeholder="Body ZH" value={f.body_zh} onChange={(e) => setF({ ...f, body_zh: e.target.value })} className={inputCls} />
        <textarea rows={6} placeholder="Body EN" value={f.body_en} onChange={(e) => setF({ ...f, body_en: e.target.value })} className={inputCls} />
        <input placeholder="Cover image URL" value={f.cover_image || ""} onChange={(e) => setF({ ...f, cover_image: e.target.value })} className={inputCls} />
        <select value={f.category} onChange={(e) => setF({ ...f, category: e.target.value })} className={inputCls} data-testid="af-category">
          {cats.map((c) => <option key={c.key} value={c.key}>{c.name_zh} / {c.name_en}</option>)}
        </select>
      </div>
      <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={f.active} onChange={(e) => setF({ ...f, active: e.target.checked })} /> {t("common.active")}</label>
      <div className="flex gap-2 pt-2">
        <button disabled={saving} className="inline-flex items-center gap-2 px-5 py-2 rounded-full bg-[#005870] text-white font-medium hover:bg-[#003645] disabled:opacity-60" data-testid="af-save">
          {saving && <Loader2 size={14} className="animate-spin" />}{t("common.save")}
        </button>
        <button type="button" onClick={onCancel} className="px-5 py-2 rounded-full border border-[#e5ebeb]">{t("common.cancel")}</button>
      </div>
    </form>
  );
}

export default function AdminArticles() {
  const { t } = useTranslation();
  const [items, setItems] = useState([]);
  const [cats, setCats] = useState([]);
  const [editing, setEditing] = useState(null);
  const load = () => {
    api.get("/articles", { params: { include_inactive: true } }).then((r) => setItems(r.data));
    api.get("/article-categories").then((r) => setCats(r.data));
  };
  useEffect(load, []);
  const del = async (id) => { if (!window.confirm("Delete?")) return; await api.delete(`/admin/articles/${id}`); load(); };

  return (
    <div className="p-6 md:p-10" data-testid="admin-articles-page">
      <div className="flex items-center justify-between">
        <h1 className="font-serif-cn text-3xl text-[#003645] font-semibold">{t("adminP3.articles")}</h1>
        {!editing && (
          <button onClick={() => setEditing({})} className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#005870] text-white text-sm font-medium hover:bg-[#003645]" data-testid="new-article-btn">
            <Plus size={14} /> {t("adminP3.newArticle")}
          </button>
        )}
      </div>
      {editing && <div className="mt-6"><ArticleForm initial={editing.id ? editing : null} cats={cats} onCancel={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} /></div>}
      {!editing && (
        <div className="mt-6 grid md:grid-cols-2 gap-4">
          {items.map((a) => (
            <div key={a.id} className="rounded-lg border border-[#e5ebeb] bg-white p-4 flex gap-3" data-testid={`admin-article-${a.id}`}>
              {a.cover_image && <div className="h-16 w-20 rounded overflow-hidden bg-[#f5f0e8]"><img src={resolveImage(a.cover_image)} alt="" className="h-full w-full object-cover" /></div>}
              <div className="flex-1 min-w-0">
                <div className="font-medium text-[#003645] truncate">{a.title_zh}</div>
                <div className="text-xs text-[#4a6e77]">{a.category} · {a.active ? "active" : "inactive"}</div>
              </div>
              <button onClick={() => setEditing(a)} className="text-[#005870] hover:text-[#00868e]"><Edit size={14} /></button>
              <button onClick={() => del(a.id)} className="text-red-700"><Trash2 size={14} /></button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
